import { HttpService } from './http';
// sorted by title, with numbers read as such (e.g. "Ligne 2" before "Ligne 10"), as the backend
// returns the networks and lines in no particular order
const titleCollator = new Intl.Collator('fr', {
  numeric: true,
  sensitivity: 'base'
});
function compareByTitle(a, b) {
  return titleCollator.compare(a.title, b.title) || a.id - b.id;
}
function parseStructuringNetworkSet(set) {
  return {
    ...set,
    networks: set.networks.map(network => ({
      ...network,
      lines: [...network.lines].sort(compareByTitle)
    })).sort(compareByTitle)
  };
}
export class StructuringNetworkService {
  static async list({
    partnerId,
    partnerCode
  }) {
    try {
      const response = await HttpService.get('v1', `/partners/${partnerId}/structuring_networks`, [], [], null, {
        partner: partnerCode
      });
      return {
        ...response,
        results: response.results.map(parseStructuringNetworkSet)
      };
    } catch (err) {
      console.error('[StructuringNetworkService][list]', err);
      throw err;
    }
  }
  static async getById({
    partnerId,
    structuringNetworkSetId,
    partnerCode
  }) {
    try {
      return parseStructuringNetworkSet(await HttpService.get('v1', `/partners/${partnerId}/structuring_network_set/${structuringNetworkSetId}`, [], [], null, {
        partner: partnerCode
      }));
    } catch (err) {
      console.error('[StructuringNetworkService][getById]', err);
      throw err;
    }
  }
  static async createWithFile({
    partnerId,
    partnerCode,
    document,
    title,
    classificationField,
    lineField
  }) {
    try {
      const formData = new FormData();
      formData.append('document', document);
      formData.append('title', title);
      if (classificationField) formData.append('classification_field', classificationField);
      if (lineField) formData.append('line_field', lineField);
      return parseStructuringNetworkSet(await HttpService.post('v1', `/partners/${partnerId}/structuring_networks_with_file`, [], [], formData, {
        partner: partnerCode
      }));
    } catch (err) {
      console.error('[StructuringNetworkService][createWithFile]', err);
      throw err;
    }
  }
  static async delete({
    partnerId,
    structuringNetworkSetId,
    partnerCode
  }) {
    try {
      return await HttpService.delete('v1', `/partners/${partnerId}/structuring_network_set/${structuringNetworkSetId}`, [], [], null, {
        partner: partnerCode
      });
    } catch (err) {
      console.error('[StructuringNetworkService][delete]', err);
      throw err;
    }
  }
  static async updateLine({
    partnerId,
    partnerCode,
    structuringNetworkLineId,
    geometry,
    title,
    color
  }) {
    try {
      // JSON.stringify drops the undefined fields: only the given ones are updated
      return await HttpService.patch('v1', `/partners/${partnerId}/structuring_network_lines/${structuringNetworkLineId}`, [], [], JSON.stringify({
        geometry,
        title,
        color
      }), {
        partner: partnerCode
      });
    } catch (err) {
      console.error('[StructuringNetworkService][updateLine]', err);
      throw err;
    }
  }
  static async getStructuringNetworkSegmentScores({
    partner,
    structuringNetworkSetId,
    structuringNetworkId,
    structuringNetworkLineId
  }) {
    try {
      const queryParams = [];
      if (structuringNetworkSetId) queryParams.push({
        key: 'network_set_id',
        value: structuringNetworkSetId
      });
      if (structuringNetworkId) queryParams.push({
        key: 'network_id',
        value: structuringNetworkId
      });
      if (structuringNetworkLineId) queryParams.push({
        key: 'network_line_id',
        value: structuringNetworkLineId
      });
      return await HttpService.get('v1', `/partners/${partner.id}/structuring_network_segment_scores`, queryParams, [], null, {
        partner: partner.code
      });
    } catch (err) {
      console.error('[StructuringNetworkService][getStructuringNetworkSegmentScores]', err);
      throw err;
    }
  }
  static async getStructuringNetworkDiscontinuityScores({
    partner,
    structuringNetworkSetId,
    structuringNetworkId,
    structuringNetworkLineId
  }) {
    try {
      const queryParams = [];
      if (structuringNetworkSetId) queryParams.push({
        key: 'network_set_id',
        value: structuringNetworkSetId
      });
      if (structuringNetworkId) queryParams.push({
        key: 'network_id',
        value: structuringNetworkId
      });
      if (structuringNetworkLineId) queryParams.push({
        key: 'network_line_id',
        value: structuringNetworkLineId
      });
      return await HttpService.get('v1', `/partners/${partner.id}/structuring_network_discontinuity_scores`, queryParams, [], null, {
        partner: partner.code
      });
    } catch (err) {
      console.error('[StructuringNetworkService][getStructuringNetworkDiscontinuityScores]', err);
      throw err;
    }
  }
  static async getStructuringNetworkSegments({
    partner,
    structuringNetworkSetId,
    structuringNetworkId,
    structuringNetworkLineId
  }) {
    try {
      const queryParams = [];
      if (structuringNetworkSetId) queryParams.push({
        key: 'network_set_id',
        value: structuringNetworkSetId
      });
      if (structuringNetworkId) queryParams.push({
        key: 'network_id',
        value: structuringNetworkId
      });
      if (structuringNetworkLineId) queryParams.push({
        key: 'network_line_id',
        value: structuringNetworkLineId
      });
      return await HttpService.get('v1', `/partners/${partner.id}/structuring_network_segments`, queryParams, [], null, {
        partner: partner.code
      });
    } catch (err) {
      console.error('[StructuringNetworkService][getStructuringNetworkSegments]', err);
      throw err;
    }
  }
  static async getStructuringNetworkSectionPaths({
    partner,
    structuringNetworkSetId,
    structuringNetworkId,
    structuringNetworkLineId
  }) {
    try {
      const queryParams = [];
      if (structuringNetworkSetId) queryParams.push({
        key: 'network_set_id',
        value: structuringNetworkSetId
      });
      if (structuringNetworkId) queryParams.push({
        key: 'network_id',
        value: structuringNetworkId
      });
      if (structuringNetworkLineId) queryParams.push({
        key: 'network_line_id',
        value: structuringNetworkLineId
      });
      return await HttpService.get('v1', `/partners/${partner.id}/structuring_network_section_paths`, queryParams, [], null, {
        partner: partner.code
      });
    } catch (err) {
      console.error('[StructuringNetworkService][getStructuringNetworkSectionPaths]', err);
      throw err;
    }
  }
}